import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Rocket, AlertCircle } from 'lucide-react';

const createProjectSchema = z.object({
  name: z.string().min(2, 'Project name must be at least 2 characters'),
  description: z.string().optional(),
  total_shares: z.coerce.number().min(1000, 'Total shares must be at least 1,000'),
  option_pool_percentage: z.coerce.number().min(0).max(100, 'Option pool must be between 0 and 100%')
});

type CreateProjectForm = z.infer<typeof createProjectSchema>;

interface CreateProjectStepProps {
  data: any;
  onUpdate: (data: any) => void;
  onNext: () => void;
  organizationId?: string;
}

export function CreateProjectStep({ data, onUpdate, onNext, organizationId }: CreateProjectStepProps) {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<CreateProjectForm>({
    resolver: zodResolver(createProjectSchema),
    defaultValues: {
      name: data.name || '',
      description: data.description || '',
      total_shares: data.total_shares || 10000000,
      option_pool_percentage: data.option_pool_percentage || 20
    }
  });

  const onSubmit = async (formData: CreateProjectForm) => {
    setIsLoading(true);
    setError(null);

    try {
      // Create project using the create_project_with_owner function
      const { data: project, error: projectError } = await supabase
        .rpc('create_project_with_owner', {
          p_organization_id: organizationId || undefined,
          p_name: formData.name,
          p_description: formData.description || '',
          p_company_name: formData.name,
          p_user_id: user!.id
        });

      if (projectError) throw projectError;

      // Update getting started checklist
      await supabase
        .from('users')
        .update({
          getting_started_checklist: {
            created_project: true
          }
        })
        .eq('id', user!.id);

      // Track analytics
      await supabase
        .from('onboarding_analytics')
        .insert({
          user_id: user!.id,
          event_name: 'project_created',
          event_data: {
            project_id: (project as any)?.id,
            organization_id: organizationId
          }
        });

      // Update step data
      onUpdate({
        ...formData,
        projectId: (project as any)?.id
      });

      onNext();
    } catch (err: any) {
      console.error('Error creating project:', err);
      setError(err.message || 'Failed to create project');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Rocket className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold">Create your first project</h3>
          <p className="text-sm text-muted-foreground">
            Projects help you organize equity for different ventures or funding rounds
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Project Name *</Label>
          <Input
            id="name"
            placeholder="Main Product, Series A, etc."
            {...register('name')}
            disabled={isLoading}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            placeholder="What is this project for?"
            rows={3}
            {...register('description')}
            disabled={isLoading}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="total_shares">Total Authorized Shares *</Label>
            <Input
              id="total_shares"
              type="number"
              placeholder="10000000"
              {...register('total_shares')}
              disabled={isLoading}
            />
            {errors.total_shares && (
              <p className="text-sm text-destructive">{errors.total_shares.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Common practice is 10 million shares
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="option_pool_percentage">Option Pool % *</Label>
            <Input
              id="option_pool_percentage"
              type="number"
              step="0.1"
              placeholder="20"
              {...register('option_pool_percentage')}
              disabled={isLoading}
            />
            {errors.option_pool_percentage && (
              <p className="text-sm text-destructive">{errors.option_pool_percentage.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Typically 15-20% for startups
            </p>
          </div>
        </div>

        <div className="bg-muted/50 p-4 rounded-lg space-y-2">
          <h4 className="font-medium text-sm">What happens next?</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>Your project will be created with the specified share structure</li>
            <li>You'll be set as the primary founder with initial equity</li>
            <li>Option pool will be reserved for future grants</li>
            <li>You can add team members and allocate equity</li>
          </ul>
        </div>

        <div className="pt-4">
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Creating Project...' : 'Create Project'}
          </Button>
        </div>
      </form>
    </div>
  );
}
